import React, { createContext, PropsWithChildren, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { getApiBase } from "../utils/watchlist-api";
import { useRouter } from "next/router";
type AuthValues = {
  token: string;
  environment: "dev" | "stage" | "test" | "prod" | "mock";
  isHandshakeAuth?: boolean;
};

export type IBUser = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: string;
  defaultMarket: string;
};

const TokenContext = createContext<AuthValues | null>(null);

export const AuthContext = ({
  children,
  token,
  environment,
  isHandshakeAuth = false,
}: PropsWithChildren<AuthValues>) => {
  // Set up axios interceptor for authentication
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const authToken = token || Cookies.get("auth_token");
      if (authToken) {
        config.headers.Authorization = `Bearer ${authToken}`;
      }
      return config;
    });

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, [token]);

  const contextValue = {
    token,
    environment,
    isHandshakeAuth,
  };

  return (
    <TokenContext.Provider value={contextValue}>
      {children}
    </TokenContext.Provider>
  );
};

export const useToken = () => {
  const context = React.useContext(TokenContext);
  if (context === null) {
    throw new Error("useToken must be used within a AuthContext");
  }
  return context;
};

export const useAuth = () => {
  const router = useRouter();
  const login = async (
    email: string,
    password: string,
    env: string,
    overrideToken?: string
  ): Promise<string> => {
    const INSIGHTS_API_BASE = getApiBase(env);

    try {
      const config = overrideToken
        ? {
            headers: {
              Authorization: `Bearer ${overrideToken}`,
            },
          }
        : undefined;

      const response = await axios.post(
        `${INSIGHTS_API_BASE}/user/login`,
        {
          email,
          password,
        },
        {
          headers: {
            Authorization: null,
          },
        }
      );

      const { token } = response.data;

      // Store the token in cookies with secure settings
      Cookies.set("auth_token", token, {
        expires: 7, // 7 days
        secure: process.env.NODE_ENV === "production",
        sameSite: "lax", // Changed from 'strict' to 'lax' to allow redirect scenarios
        path: "/",
      });

      return token;
    } catch (error) {
      console.error("Login error:", error);
      throw new Error("Invalid credentials");
    }
  };

  const getUserRole = () => {
    const token = Cookies.get("auth_token");
    if (!token) {
      return { role: "", username: "", id: "" };
    }
    const [, payload] = token.split(".");
    const decodedPayload = Buffer.from(payload, "base64").toString();
    const { role, username, id, permissions, maxWatchLists } =
      JSON.parse(decodedPayload);

    return {
      role,
      username,
      id,
      permissions,
      maxWatchLists: maxWatchLists || 3,
    };
  };

  const logout = () => {
    Cookies.remove("auth_token", { path: "/" });
    router.push("/login");
  };

  return { login, logout, getUserRole };
};
